/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-07-24
 */

// Add third-party dependencies.
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Fade,
  Zoom,
  Backdrop,
  LinearProgress,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  CheckCircle,
  Error,
} from '@mui/icons-material';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';

// Add local dependencies.
import {
  useSessionLoginMutation,
  setCredentials,
  setSessionExpiry,
  takeSignedOutMarker,
} from '../../../features/auth';
import { calculateSessionExpiry } from '../../../utils/sessionAuth';
import { useFormValidation } from '../../../hooks/useFormValidation';
import { loginSchema } from '../../../utils/validationSchemas';
import { useSuccessNotification } from '../../../hooks/useSuccessNotification';
import { accessibilityUtils, ARIA_ROLES } from '../../../utils/accessibility';
import LiveRegion from '../../common/LiveRegion';

interface FormData {
  email: string;
  password: string;
  rememberMe: boolean;
}

/**
 * Login form component for user authentication.
 * @example
 * return <LoginForm />
 * @returns {React.ReactElement} The login form component.
 */
const LoginForm: React.FC = () => {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [login, { isLoading }] = useSessionLoginMutation();
  const { showLoginSuccess } = useSuccessNotification();

  const [formData, setFormData] = useState<FormData>({
    email: '',
    password: '',
    rememberMe: false,
  });

  const [showPassword, setShowPassword] = useState(false);
  const [apiError, setApiError] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [verificationRequired, setVerificationRequired] = useState(false);
  const [fieldValidationStates, setFieldValidationStates] = useState<{
    email: 'idle' | 'valid' | 'invalid';
    password: 'idle' | 'valid' | 'invalid';
  }>({
    email: 'idle',
    password: 'idle',
  });

  // Initialize form validation with login schema
  const {
    errors,
    validateForm,
    handleInputChange: handleValidationChange,
  } = useFormValidation<FormData>(loginSchema, { validateOnBlur: true });

  // Generate unique IDs for accessibility
  const formId = accessibilityUtils.generateId('login-form');
  const emailErrorId = accessibilityUtils.generateId('email-error');
  const passwordErrorId = accessibilityUtils.generateId('password-error');

  // Get location state for success messages and redirect handling
  const location = useLocation();

  // Check for success message from email verification
  useEffect(() => {
    const state = location.state as { message?: string };
    if (state?.message) {
      setSuccessMessage(state.message);
      // Clear the state to prevent showing the message on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;

    setFormData(prev => ({
      ...prev,
      [name]: newValue,
    }));

    // Use validation hook for error handling
    const updatedFormData = {
      ...formData,
      [name]: newValue,
    };
    handleValidationChange(name as keyof FormData, newValue, updatedFormData);

    // Update field validation states for visual feedback
    if (name === 'email' || name === 'password') {
      const fieldName = name as 'email' | 'password';
      const hasError = errors[fieldName];
      const hasValue =
        typeof newValue === 'string' && newValue.trim().length > 0;

      let validationState: 'idle' | 'valid' | 'invalid' = 'idle';
      if (hasError) {
        validationState = 'invalid';
      } else if (hasValue) {
        validationState = 'valid';
      }

      setFieldValidationStates(prev => ({
        ...prev,
        [fieldName]: validationState,
      }));
    }
  };

  /**
   * Gets the validation icon for a field based on its state.
   */
  const getValidationIcon = (fieldName: 'email' | 'password') => {
    const state = fieldValidationStates[fieldName];
    const hasError = !!errors[fieldName];

    if (state === 'idle') return null;

    if (hasError) {
      return (
        <Zoom in>
          <Error color="error" sx={{ fontSize: 20 }} />
        </Zoom>
      );
    }

    if (state === 'valid') {
      return (
        <Zoom in>
          <CheckCircle color="success" sx={{ fontSize: 20 }} />
        </Zoom>
      );
    }

    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setApiError('');
    setVerificationRequired(false);
    setIsSubmitting(true);

    if (!validateForm(formData)) {
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await login({
        email: formData.email,
        password: formData.password,
        remember_me: formData.rememberMe,
      }).unwrap();

      // Update Redux state with user and CSRF token
      dispatch(
        setCredentials({
          user: response.user,
          csrfToken: response.csrf_token,
        })
      );

      // Set session expiry based on "remember me" choice
      const sessionDuration = formData.rememberMe ? 30 * 24 * 60 : 60; // 30 days or 1 hour
      dispatch(setSessionExpiry(calculateSessionExpiry(sessionDuration)));

      // Show success notification with user's name
      showLoginSuccess(response.user.first_name);

      // Check if there's a redirect URL from protected route. The first
      // sign-in after a sign-out in this tab skips it: Back may have put the
      // previous user's page there (FE todo/43, todo/42 decision C).
      const signedOut = takeSignedOutMarker();
      const from = (location.state as { from?: unknown } | null)?.from;
      const redirectTo =
        !signedOut && typeof from === 'string' ? from : '/dashboard';

      // Redirect to the intended destination, else the dashboard (todo/29)
      navigate(redirectTo, { replace: true });
    } catch (error: any) {
      // Check for email verification required error
      if (error.data?.verification_required) {
        setVerificationRequired(true);
        setApiError(error.data.error);
      } else if (error.data?.detail) {
        setApiError(error.data.detail);
      } else if (error.data?.error) {
        setApiError(error.data.error);
      } else if (error.data?.email) {
        setApiError(error.data.email[0]);
      } else if (error.data?.password) {
        setApiError(error.data.password[0]);
      } else {
        setApiError(t('login.errors.loginFailed'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box sx={{ position: 'relative' }}>
      {/* Screen reader announcements */}
      <LiveRegion
        message={(() => {
          if (isSubmitting) return t('login.loading.pleaseWait');
          if (successMessage) return `Success: ${successMessage}`;
          if (apiError) return `Error: ${apiError}`;
          return '';
        })()}
        politeness={apiError ? 'ASSERTIVE' : 'POLITE'}
      />

      {/* Loading Progress Bar */}
      {(isLoading || isSubmitting) && (
        <LinearProgress
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            borderRadius: '4px 4px 0 0',
          }}
        />
      )}

      {/* Loading Backdrop */}
      <Backdrop
        sx={{
          position: 'absolute',
          zIndex: 1,
          backgroundColor: 'rgba(255, 255, 255, 0.7)',
          borderRadius: 1,
        }}
        open={isSubmitting}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <CircularProgress size={40} />
          <Typography variant="body2" color="text.secondary">
            {t('login.loading.signingIn')}
          </Typography>
        </Box>
      </Backdrop>

      <Box
        component="form"
        onSubmit={handleSubmit}
        noValidate
        role={ARIA_ROLES.FORM}
        aria-label={t('login.accessibility.formLabel')}
        id={formId}
      >
        <Fade in={!!successMessage}>
          <Box>
            {successMessage && (
              <Alert
                severity="success"
                data-testid="success-message"
                sx={{
                  mb: 2,
                  '& .MuiAlert-icon': {
                    fontSize: '1.5rem',
                  },
                }}
                icon={<CheckCircle />}
              >
                {successMessage}
              </Alert>
            )}
          </Box>
        </Fade>

        <Fade in={!!apiError}>
          <Box>
            {apiError && (
              <Alert
                severity="error"
                data-testid="error-message"
                sx={{
                  mb: 2,
                  '& .MuiAlert-icon': {
                    fontSize: '1.5rem',
                  },
                }}
              >
                {apiError}
                {verificationRequired && (
                  <Box sx={{ mt: 2 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() =>
                        navigate('/verify-email', {
                          state: { email: formData.email },
                        })
                      }
                      sx={{ textTransform: 'none' }}
                    >
                      {t('login.buttons.resendVerification')}
                    </Button>
                  </Box>
                )}
              </Alert>
            )}
          </Box>
        </Fade>

        <TextField
          fullWidth
          label={t('login.form.label.email')}
          name="email"
          data-testid="email"
          type="email"
          value={formData.email}
          onChange={handleInputChange}
          error={!!errors.email}
          helperText={errors.email}
          disabled={isLoading || isSubmitting}
          required
          aria-required
          aria-invalid={!!errors.email}
          aria-describedby={errors.email ? emailErrorId : undefined}
          FormHelperTextProps={{
            id: errors.email ? emailErrorId : undefined,
          }}
          autoComplete="email"
          InputProps={{
            endAdornment: getValidationIcon('email'),
          }}
          sx={{
            mb: 2,
            '& .MuiOutlinedInput-root': {
              '&:hover fieldset': {
                borderColor: 'primary.main',
                borderWidth: '2px',
              },
              '&.Mui-focused fieldset': {
                borderWidth: '2px',
              },
              transition: 'all 0.2s ease-in-out',
            },
          }}
        />

        <TextField
          fullWidth
          label={t('login.form.label.password')}
          name="password"
          data-testid="password"
          type={showPassword ? 'text' : 'password'}
          value={formData.password}
          onChange={handleInputChange}
          error={!!errors.password}
          helperText={errors.password}
          disabled={isLoading || isSubmitting}
          required
          aria-required
          aria-invalid={!!errors.password}
          aria-describedby={errors.password ? passwordErrorId : undefined}
          FormHelperTextProps={{
            id: errors.password ? passwordErrorId : undefined,
          }}
          autoComplete="current-password"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {getValidationIcon('password')}
                <IconButton
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                  disabled={isLoading || isSubmitting}
                  aria-label={t('login.accessibility.passwordToggle')}
                  sx={{ ml: 0.5 }}
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            mb: 2,
            '& .MuiOutlinedInput-root': {
              '&:hover fieldset': {
                borderColor: 'primary.main',
                borderWidth: '2px',
              },
              '&.Mui-focused fieldset': {
                borderWidth: '2px',
              },
              transition: 'all 0.2s ease-in-out',
            },
          }}
        />

        <FormControlLabel
          control={
            <Checkbox
              name="rememberMe"
              checked={formData.rememberMe}
              onChange={handleInputChange}
              disabled={isLoading}
            />
          }
          label={
            <Typography variant="body2">
              {t('login.form.rememberMe')}
            </Typography>
          }
          sx={{ mb: 2 }}
        />

        <Button
          type="submit"
          fullWidth
          variant="contained"
          size="large"
          disabled={isLoading}
          data-testid="login-submit"
          sx={{ mt: 1, mb: 2 }}
        >
          {isLoading ? <CircularProgress size={24} /> : t('login.form.submit')}
        </Button>

        <Box sx={{ textAlign: 'center' }}>
          <Link
            to="/forgot-password"
            style={{
              textDecoration: 'none',
              color: 'inherit',
              marginBottom: '16px',
              display: 'block',
            }}
          >
            {t('login.links.forgotPassword')}
          </Link>

          <Typography variant="body2">
            {t('login.links.noAccount')}{' '}
            <Link
              to="/register"
              style={{ textDecoration: 'none', color: 'inherit' }}
            >
              {t('login.links.createAccount')}
            </Link>
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default LoginForm;
